/**
 * What happened to her today.
 *
 * PERSONA.md has always claimed the appliances are her informants and that she
 * has been in this flat for longer than anyone living in it — but nothing ever
 * *happened* to her. Lore that is only asserted is set dressing, and a narrator
 * who has no day of her own can only ever comment on yours. That asymmetry is
 * most of what makes a line catalogue read as a line catalogue.
 *
 * So: two seeded, storage-free layers. A **day event** is a small household
 * incident she mentions in passing, drawn from the date — roughly one day in
 * three, so it stays news rather than becoming the new formula. A **saga
 * chapter** is one beat of a running bit that advances a chapter every few
 * weeks, so someone paying attention across a month gets a story and someone
 * who isn't loses nothing.
 *
 * Nothing is stored. The same account on the same date always gets the same
 * answer, which is what lets the greeting pin itself for the day and the push
 * sender agree with the app without a shared table.
 */

import { daysBetween, startOfWeek } from './date.js'
import { hashSeed, mulberry32 } from './sass.js'

/**
 * Small things that happen in a flat, told from the point of view of whoever
 * has been watching it since 1863. Ids only — the lines live in the app's
 * catalogue, keyed `greeting.<id>`, the same way every other pool does.
 */
export const HER_DAY_EVENTS: readonly string[] = [
  'day.orkaite',
  'day.saldytuvas',
  'day.svarstykles',
  'day.virdulys',
  'day.indaplove',
  'day.skalbykle',
  'day.langas',
  'day.grindys',
  'day.kaimynai',
  'day.laikrodis',
  'day.elektra',
  'day.lietus',
  'day.lempa',
  'day.duonine',
  'day.puodas',
  'day.veidrodis',
  'day.kaitlente',
]

/** A running bit and how many chapters it has before it starts over. */
export interface Saga {
  id: string
  chapters: number
}

/**
 * Seven bits, spread across the three things she can have an opinion about:
 * the household (the sponge, the missing teaspoon), herself (the vacuum, the
 * smell, the hum from the fridge), and the work itself (the count, the dust).
 *
 * The number stopped mattering the way it used to when the rotation changed —
 * see `sagaBeat`. Under the old turn-taking scheme every extra saga made every
 * individual arc slower, which capped the cast of bits at three. Arcs now run
 * to their end before the next one starts, so this list can grow without any
 * one story taking a year to finish.
 */
export const SAGAS: readonly Saga[] = [
  { id: 'saga.kempine', chapters: 5 },
  { id: 'saga.siurblys', chapters: 5 },
  { id: 'saga.skaicius', chapters: 5 },
  { id: 'saga.gaudesys', chapters: 5 },
  { id: 'saga.saukstelis', chapters: 5 },
  { id: 'saga.kvapas', chapters: 5 },
  { id: 'saga.dulkes', chapters: 5 },
]

/** Roughly one day in eight carries a saga beat… */
const SAGA_RATE = 0.12
/** …and, failing that, roughly one in four carries an ordinary day event. */
const DAY_RATE = 0.26

/**
 * How often a day that has something to react to actually gets reacted to.
 * Not every time: a narrator who comments on every empty yesterday is a
 * reminder, and she is not one.
 */
const REACT_RATE = 0.5

/**
 * Yesterday, as three facts she might have an opinion about.
 *
 * Deliberately about a *finished* day. A fact that could change while the day
 * is still being lived would make her day change under her, and would break
 * the one promise this module makes — that the app and the lock screen, asked
 * on the same date, give the same answer.
 */
export interface HerLifeFacts {
  /** Nothing at all was written down yesterday. */
  yesterdayEmpty: boolean
  /** Yesterday's protein target was met. */
  yesterdayProteinHit: boolean
  /** Yesterday went over budget. */
  yesterdayOver: boolean
}

/**
 * Which reaction yesterday earned, if any. Priority rather than a roll: an
 * empty day is the loudest thing that can happen to her, and a protein target
 * met is rarer than a day over budget, so it goes second or it would never be
 * heard.
 */
function reactionTo(facts: HerLifeFacts | undefined): string | null {
  if (facts == null) return null
  if (facts.yesterdayEmpty) return 'react.tuscia'
  if (facts.yesterdayProteinHit) return 'react.baltymai'
  if (facts.yesterdayOver) return 'react.persivalgyta'
  return null
}

/** A Monday, so week indices come out whole. */
const WEEK_EPOCH = '2026-01-05'

function weekIndex(date: string): number {
  // Offset well past any date this app will see, so pre-epoch dates in tests
  // stay positive rather than folding a negative modulo.
  return Math.floor(daysBetween(WEEK_EPOCH, startOfWeek(date)) / 7) + 5200
}

/** Every chapter of every saga, laid end to end. */
const TOTAL_CHAPTERS = SAGAS.reduce((sum, saga) => sum + saga.chapters, 0)

/**
 * The saga beat for a given week: one bit runs to its ending before the next
 * one starts.
 *
 * This used to take turns — saga A this week, B the next, C the one after,
 * back to A's second chapter in three weeks' time. That kept every bit
 * simmering, and it meant a five-part story took fifteen weeks to tell and
 * three separate stories were half-finished at any moment. Nobody follows
 * three interleaved plots told one sentence a fortnight.
 *
 * Now a saga gets consecutive weeks until it is over. A bit arrives, develops
 * for a month, ends, and something else starts. That is a shape a person can
 * actually notice, and it gives the finale a definite week, which is what the
 * ceremony host needs to know when to mark the ending.
 *
 * The per-account offset keeps two people sharing a phone out of lockstep.
 */
export function sagaBeat(userId: string, date: string): string {
  let position = (weekIndex(date) + (hashSeed(userId) % TOTAL_CHAPTERS)) % TOTAL_CHAPTERS
  for (const saga of SAGAS) {
    if (position < saga.chapters) return `${saga.id}.${position}`
    position -= saga.chapters
  }
  // Unreachable: the modulo above bounds `position` below the total.
  return `${SAGAS[0]!.id}.0`
}

/** True when this beat is the last chapter of its saga — an ending, not a beat. */
export function isSagaFinale(beat: string): boolean {
  const cut = beat.lastIndexOf('.')
  const id = beat.slice(0, cut)
  const chapter = Number(beat.slice(cut + 1))
  const saga = SAGAS.find((entry) => entry.id === id)
  return saga != null && chapter === saga.chapters - 1
}

/**
 * What she has to say about herself today, or null on an ordinary day when the
 * answer is honestly nothing.
 *
 * `facts` are optional and, when they are absent, this returns exactly what it
 * returned before reactions existed — which is what keeps a client that has
 * them and a push server that has not yet computed them from disagreeing about
 * an ordinary day.
 */
export function herLifeFor(userId: string, date: string, facts?: HerLifeFacts): string | null {
  // Something that actually happened outranks the dice. Checked first so a
  // reaction cannot be crowded out by a saga week, and rolled anyway so she
  // does not respond to every single empty yesterday.
  const reaction = reactionTo(facts)
  if (reaction != null && mulberry32(hashSeed(`${userId}:react:${date}`))() < REACT_RATE) {
    return reaction
  }

  const roll = mulberry32(hashSeed(`${userId}:herlife:${date}`))()
  if (roll < SAGA_RATE) return sagaBeat(userId, date)
  if (roll < SAGA_RATE + DAY_RATE) {
    const pick = mulberry32(hashSeed(`${userId}:herday:${date}`))()
    return HER_DAY_EVENTS[Math.floor(pick * HER_DAY_EVENTS.length) % HER_DAY_EVENTS.length]!
  }
  return null
}
